import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Activity,
  CheckIn,
  ExportBundle,
  Goal,
  Note,
  SettingsMap,
  Tag,
  TodaySnapshot,
  User,
  WeeklyReview,
} from '@otakotakdev/shared';
import { api, ApiError } from '@/lib/api';

export interface SessionInfo {
  user: (User & { discordUserId?: string }) | null;
  expiresAt?: string;
}

export function useSession() {
  return useQuery<SessionInfo>({
    queryKey: ['session'],
    queryFn: () => api.get<SessionInfo>('/auth/session'),
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password: string) =>
      api.post<{ user: { id: string; displayName: string } }>('/auth/login', { password }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session'] });
    },
  });
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Rotate the dashboard password. The server revokes every session and issues
 * a fresh cookie for this browser, so the session cache is refreshed on
 * success. Passwords are never stored client-side.
 */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.patch<{ message: string }>('/auth/password', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session'] });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ loggedOut: boolean }>('/auth/logout'),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useToday() {
  return useQuery<TodaySnapshot>({
    queryKey: ['today'],
    queryFn: () => api.get<TodaySnapshot>('/today'),
    staleTime: 30_000,
  });
}

export function useGoals(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['goals', params],
    queryFn: () => api.list<Goal>('/goals', params),
  });
}

export function useGoal(id: string | null) {
  return useQuery<Goal & { activities: Activity[] }>({
    queryKey: ['goal', id],
    queryFn: () => api.get<Goal & { activities: Activity[] }>(`/goals/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<Goal>('/goals', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['goals'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      api.patch<Goal>(`/goals/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['goals'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/goals/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['goals'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useActivities(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['activities', params],
    queryFn: () => api.list<Activity>('/activities', params),
  });
}

export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<Activity>('/activities', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activities'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      api.patch<Activity>(`/activities/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activities'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/activities/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activities'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useNotes(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['notes', params],
    queryFn: () => api.list<Note>('/notes', params),
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<Note>('/notes', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notes'] });
      void qc.invalidateQueries({ queryKey: ['today'] });
    },
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      api.patch<Note>(`/notes/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/notes/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useTags() {
  return useQuery<Tag[]>({
    queryKey: ['tags'],
    queryFn: () => api.get<Tag[]>('/tags'),
  });
}

export function useCheckIns(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ['check-ins', params],
    queryFn: () => api.list<CheckIn>('/check-ins', params),
  });
}

export function useCreateCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<CheckIn>('/check-ins', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['check-ins'] });
    },
  });
}

export function useWeeklyReview(weekStart?: string) {
  return useQuery<WeeklyReview>({
    queryKey: ['review', weekStart ?? 'current'],
    queryFn: () => api.get<WeeklyReview>('/reviews/weekly', { weekStart }),
  });
}

export function useSettings() {
  return useQuery<{ settings: SettingsMap; user: SessionInfo['user'] }>({
    queryKey: ['settings'],
    queryFn: () => api.get<{ settings: SettingsMap; user: SessionInfo['user'] }>('/settings'),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ settings: SettingsMap }>('/settings', patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

export function useSearch(q: string, type = 'all') {
  return useQuery({
    queryKey: ['search', q, type],
    queryFn: () =>
      api.get<{ goals: Goal[]; notes: Note[]; activities: Activity[] }>('/search', { q, type }),
    enabled: q.trim().length > 0,
  });
}

export function useExportUrl(kind: 'json' | 'csv', type?: string): string {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';
  return kind === 'json' ? `${base}/export/json` : `${base}/export/csv?type=${type ?? 'goals'}`;
}

export type { ExportBundle, ApiError };
